import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  ArrowUpRight,
  Bookmark,
  BookmarkCheck,
  Share2,
  MessageCircleMore,
  Plus,
  Minus,
} from "lucide-react";
import SingleProductLoader from "../../components/SingleProductLoader/SingleProductLoader";
import StarRating from "../../components/StarRating/StarRating";
import "./Product.css";
import { addItem, incrementQuantity, decrementQuantity, removeItem } from "../../redux/slices/cart";
import { useGetProduct } from "../../hooks/useGetProduct";
import { useFavorites } from "../../hooks/useFavorites";
import { toast } from "react-toastify";

function Product() {
  const token = useSelector((state) => state.auth.token);
  const { slug } = useParams();
  const { user } = useSelector((state) => state.auth);
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const {
    data: product,
    isLoading,
    error,
  } = useGetProduct(slug, user?.id, token);
  const { addFavoriteMutation, removeFavoriteMutation } = useFavorites(token);

  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (product && product.favorite) {
      setIsFavorite(true);
    }
  }, [product]);

  const debouncedFavorite = (actionFn) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      actionFn();
      debounceRef.current = null;
    }, 500);
  };

  // --- Handlers ---
  const addToFavoriteHandler = () => {
    if (!token) {
      navigate("/login");
      return;
    }
    setIsFavorite(true);
    debouncedFavorite(() => {
      addFavoriteMutation.mutate(product.id);
    });
  };

  const removeFromFavoriteHandler = () => {
    if (!token) return;
    setIsFavorite(false);
    debouncedFavorite(() => {
      removeFavoriteMutation.mutate(product.id);
    });
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  if (isLoading) {
    return (
      <section className="Product">
        <div className="loading-container">
          <SingleProductLoader />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="Product">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>We couldn't load the product details. Please try again later.</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </section>
    );
  }

  if (!product) {
    return (
      <section className="Product">
        <div className="not-found-container">
          <h2>Product Not Found</h2>
          <p>
            The product you're looking for doesn't exist or may have been
            removed.
          </p>
          <Link to="/" className="back-link">
            ← Back to Products
          </Link>
        </div>
      </section>
    );
  }

  const item = product;

  const hasAccess = item.meta_data?.some(
    (meta) => meta.key === "_ebook_stream_url" && !!meta.value
  );

  // Check if item is in cart
  const itemInCart = cartItems.find((cartItem) => cartItem.id === item.id);
  const itemQuantity = itemInCart?.quantity || 0;

  const addToCartHandler = ({ id, name, image, price, authorStripeId }) => {
    dispatch(addItem({ id, name, image, price, authorStripeId }));
  };

  const handleIncrement = (id) => {
    dispatch(incrementQuantity(id));
  };

  const handleDecrement = (id) => {
    const itemInCart = cartItems.find((item) => item.id === id);
    if (itemInCart && itemInCart.quantity === 1) {
      dispatch(removeItem(id));
    } else {
      dispatch(decrementQuantity(id));
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  return (
    <section className="Product">
      <div className="product-info">
        <img src={item.images?.[0]?.src} alt={item.name} />
        <div className="product-name">
          <div className="product-title">
            <h1 className="name">{item.name}</h1>
            <h1 className="price">${item.regular_price}</h1>
          </div>
          <p className="product-author">
            {item.meta_data?.find((meta) => meta.key === "author")?.value ||
              "Unknown Author"}
          </p>
          <StarRating rating={item.average_rating} />
          <p
            className="short-product-description"
            dangerouslySetInnerHTML={{ __html: item.short_description }}
          />
          <div className="product-action-buttons">
            {hasAccess ? (
              <button onClick={() => navigate("stream")}>
                Read now <ArrowUpRight />
              </button>
            ) : itemInCart ? (
              <div className="product-quantity-controls">
                <button
                  onClick={() => handleDecrement(item.id)}
                  // className="product-qty-btn"  
                >
                  <Minus size={18} />
                </button>
                <span className="product-quantity-value">{itemQuantity}</span>
                <button
                  onClick={() => handleIncrement(item.id)}
                  // className="product-qty-btn"
                >
                  <Plus size={18} />
                </button>
              </div>
            ) : (
              <button
                className="add-to-cart-btn"
                onClick={() => {
                  // Find the author_stripe_id in meta_data
                  const authorStripeMeta = item.meta_data?.find(
                    (meta) => meta.key === "author_stripe_id"
                  );

                  const authorStripeId = authorStripeMeta
                    ? authorStripeMeta.value
                    : null;

                  toast.success("Added to cart!");

                  addToCartHandler({
                    id: item.id,
                    name: item.name,
                    image: item.images?.[0]?.src,
                    price: item.regular_price,
                    authorStripeId,
                  });
                }}
              >
                <ShoppingCart size={18} /> Add to cart
              </button>
            )}
            <ul>
              <li>
                <button className="product-icon-button">
                  {isFavorite ? (
                    <BookmarkCheck
                      size={24}
                      color="white"
                      onClick={removeFromFavoriteHandler}
                    />
                  ) : (
                    <Bookmark
                      size={24}
                      color="white"
                      onClick={addToFavoriteHandler}
                    />
                  )}
                </button>
              </li>
              <li className="relative">
                <button onClick={handleShare} className="product-icon-button">
                  <Share2 size={24} color="white" />
                </button>
                {copied && <span className="tooltip">Copied!</span>}
              </li>
              <li>
                <Link to="reviews">
                  <MessageCircleMore size={24} color="white" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="product-meta">
        <div className="description">
          <h3>Description</h3>
          <div
            className="product-description"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        </div>
        <div className="meta">
          <h3>Author</h3>
          <p>
            {item.meta_data?.find((meta) => meta.key === "author")?.value ||
              "Unknown Author"}
          </p>
          <h3>Editor</h3>
          <p>
            {item.meta_data?.find((meta) => meta.key === "editor")?.value ||
              "Unknown Editor"}
          </p>
          <h3>Genre</h3>
          <p>
            {item.meta_data?.find((meta) => meta.key === "genre")?.value ||
              "Unknown Genre"}
          </p>
        </div>
      </div>
    </section>
  );
}

export default Product;
